/**
 * Plant Canvas Component
 * SVG-based canvas with zoom, pan, and background image support
 */

import React, { forwardRef, useRef, useEffect, useState, useCallback } from 'react';
import { PlantLayout } from '../../services/plantMapping';

interface PlantCanvasProps {
  layout: PlantLayout;
  zoom: number;
  centerX: number;
  centerY: number;
  backgroundFitMode?: 'cover' | 'contain' | 'stretch';
  onZoomChange: (zoom: number) => void;
  onCenterChange: (x: number, y: number) => void;
  children?: React.ReactNode;
}

const PlantCanvas = forwardRef<HTMLDivElement, PlantCanvasProps>(({
  layout,
  zoom,
  centerX,
  centerY,
  backgroundFitMode = 'cover',
  onZoomChange,
  onCenterChange,
  children
}, ref) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPoint, setLastPanPoint] = useState({ x: 0, y: 0 });
  const [viewBox, setViewBox] = useState({ x: 0, y: 0, width: 1000, height: 600 });

  // Calculate viewBox based on zoom and center
  useEffect(() => {
    const viewWidth = layout.width / zoom;
    const viewHeight = layout.height / zoom;
    const viewX = (centerX * layout.width) - (viewWidth / 2);
    const viewY = (centerY * layout.height) - (viewHeight / 2);

    setViewBox({
      x: viewX,
      y: viewY,
      width: viewWidth,
      height: viewHeight
    });
  }, [layout, zoom, centerX, centerY]);

  // Mouse wheel zoom
  const handleWheel = useCallback((event: React.WheelEvent) => {
    event.preventDefault();
    
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;

    // Get mouse position relative to SVG
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;
    
    // Convert to SVG coordinates
    const svgX = (mouseX / rect.width) * viewBox.width + viewBox.x;
    const svgY = (mouseY / rect.height) * viewBox.height + viewBox.y;
    
    // Calculate zoom factor
    const zoomFactor = event.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.max(0.1, Math.min(zoom * zoomFactor, 5));
    
    // Calculate new center to keep mouse position fixed
    const newCenterX = svgX / layout.width;
    const newCenterY = svgY / layout.height;
    
    onZoomChange(newZoom);
    onCenterChange(newCenterX, newCenterY);
  }, [zoom, viewBox, layout, onZoomChange, onCenterChange]);

  // Mouse pan start
  const handleMouseDown = useCallback((event: React.MouseEvent) => {
    if (event.button === 0) { // Left mouse button
      setIsPanning(true);
      setLastPanPoint({ x: event.clientX, y: event.clientY });
      event.preventDefault();
    }
  }, []);

  // Mouse pan move
  const handleMouseMove = useCallback((event: React.MouseEvent) => {
    if (!isPanning) return;

    const deltaX = event.clientX - lastPanPoint.x;
    const deltaY = event.clientY - lastPanPoint.y;

    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;

    // Convert pixel movement to SVG coordinates
    const svgDeltaX = (deltaX / rect.width) * viewBox.width;
    const svgDeltaY = (deltaY / rect.height) * viewBox.height;

    // Update center position
    const newCenterX = centerX - (svgDeltaX / layout.width);
    const newCenterY = centerY - (svgDeltaY / layout.height);

    // Clamp to bounds
    const clampedX = Math.max(0, Math.min(1, newCenterX));
    const clampedY = Math.max(0, Math.min(1, newCenterY));

    onCenterChange(clampedX, clampedY);
    setLastPanPoint({ x: event.clientX, y: event.clientY });
  }, [isPanning, lastPanPoint, viewBox, centerX, centerY, layout, onCenterChange]);

  // Mouse pan end
  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  // Global mouse events for panning
  useEffect(() => {
    if (isPanning) {
      const handleGlobalMouseMove = (event: MouseEvent) => {
        const deltaX = event.clientX - lastPanPoint.x;
        const deltaY = event.clientY - lastPanPoint.y;

        const rect = svgRef.current?.getBoundingClientRect();
        if (!rect) return;

        const svgDeltaX = (deltaX / rect.width) * viewBox.width;
        const svgDeltaY = (deltaY / rect.height) * viewBox.height;

        const newCenterX = centerX - (svgDeltaX / layout.width);
        const newCenterY = centerY - (svgDeltaY / layout.height);

        const clampedX = Math.max(0, Math.min(1, newCenterX));
        const clampedY = Math.max(0, Math.min(1, newCenterY));

        onCenterChange(clampedX, clampedY);
        setLastPanPoint({ x: event.clientX, y: event.clientY });
      };

      const handleGlobalMouseUp = () => {
        setIsPanning(false);
      };

      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [isPanning, lastPanPoint, viewBox, centerX, centerY, layout, onCenterChange]);

  // Convert normalized coordinates to SVG coordinates
  const normalizedToSVG = useCallback((x: number, y: number) => ({
    x: x * layout.width,
    y: y * layout.height
  }), [layout]);

  const backgroundOpacity = Math.max(0, Math.min(1, layout.background_opacity ?? 0.8));
  const backgroundPatternId = `plantBackground-${layout.id}`;
  const backendOrigin = (() => {
    try {
      const apiBaseUrl = (import.meta as any).env.VITE_API_URL || 'http://localhost:8400/api';
      return new URL(apiBaseUrl).origin;
    } catch {
      return '';
    }
  })();
  const preserveAspectRatioValue = (() => {
    if (backgroundFitMode === 'contain') return 'xMidYMid meet';
    if (backgroundFitMode === 'stretch') return 'none';
    return 'xMidYMid slice';
  })();
  const backgroundImageHref = (() => {
    const rawPath = (layout.background_image || '').trim();
    if (!rawPath) return '/images/plant_empty.png';
    if (rawPath.startsWith('http://') || rawPath.startsWith('https://')) return rawPath;
    if (rawPath.startsWith('/media/')) return backendOrigin ? `${backendOrigin}${rawPath}` : rawPath;
    if (rawPath.startsWith('/images/')) return rawPath;
    if (rawPath.startsWith('/')) return rawPath;
    if (rawPath.startsWith('images/plants/')) {
      return backendOrigin ? `${backendOrigin}/media/${rawPath}` : `/media/${rawPath}`;
    }
    if (rawPath.startsWith('images/')) return `/${rawPath}`;
    return backendOrigin ? `${backendOrigin}/media/${rawPath}` : `/media/${rawPath}`;
  })();

  return (
    <div 
      ref={ref}
      className="w-full h-full overflow-hidden bg-gray-100 relative"
      style={{ cursor: 'default' }}
    >
      <svg
        ref={svgRef}
        className="w-full h-full"
        viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`}
        style={{ userSelect: 'none' }}
      >
        {/* Background Pattern */}
        <defs>
          <pattern
            id="grid"
            width="50"
            height="50"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M 50 0 L 0 0 0 50"
              fill="none"
              stroke="#e5e7eb"
              strokeWidth="1"
            />
          </pattern>
          
          {/* Plant background image */}
          <pattern
            id={backgroundPatternId}
            patternUnits="userSpaceOnUse"
            width={layout.width}
            height={layout.height}
          >
            <image
              href={backgroundImageHref}
              x="0"
              y="0"
              width={layout.width}
              height={layout.height}
              preserveAspectRatio={preserveAspectRatioValue}
            />
          </pattern>
        </defs>

        {/* Grid Background */}
        <rect
          x="0"
          y="0"
          width={layout.width}
          height={layout.height}
          fill="url(#grid)"
        />

        {/* Plant Background Image */}
        <rect
          x="0"
          y="0"
          width={layout.width}
          height={layout.height}
          fill={`url(#${backgroundPatternId})`}
          opacity={backgroundOpacity}
        />

        {/* Plant Border */}
        <rect
          x="0"
          y="0"
          width={layout.width}
          height={layout.height}
          fill="none"
          stroke="#374151"
          strokeWidth="2"
        />

        {/* Children (zones, workshops, etc.) */}
        <g className="plant-content">
          {children}
        </g>

        {/* Coordinate Helper (visible only at high zoom) */}
        {zoom > 2 && (
          <g className="coordinate-helper" opacity="0.3">
            {/* Vertical lines every 100 units */}
            {Array.from({ length: Math.floor(layout.width / 100) + 1 }, (_, i) => (
              <line
                key={`v-${i}`}
                x1={i * 100}
                y1="0"
                x2={i * 100}
                y2={layout.height}
                stroke="#9ca3af"
                strokeWidth="0.5"
                strokeDasharray="2,2"
              />
            ))}
            
            {/* Horizontal lines every 100 units */}
            {Array.from({ length: Math.floor(layout.height / 100) + 1 }, (_, i) => (
              <line
                key={`h-${i}`}
                x1="0"
                y1={i * 100}
                x2={layout.width}
                y2={i * 100}
                stroke="#9ca3af"
                strokeWidth="0.5"
                strokeDasharray="2,2"
              />
            ))}
          </g>
        )}

        {/* Zoom Level Indicator */}
        <text
          x={viewBox.x + 10}
          y={viewBox.y + 25}
          fill="#6b7280"
          fontSize="12"
          fontFamily="monospace"
        >
          Zoom: {(zoom * 100).toFixed(0)}%
        </text>

        {/* Coordinates Indicator (for debugging) */}
        {process.env.NODE_ENV === 'development' && (
          <text
            x={viewBox.x + 10}
            y={viewBox.y + 45}
            fill="#6b7280"
            fontSize="10"
            fontFamily="monospace"
          >
            Center: ({(centerX * 100).toFixed(1)}%, {(centerY * 100).toFixed(1)}%)
          </text>
        )}
      </svg>
    </div>
  );
});

PlantCanvas.displayName = 'PlantCanvas';

export default PlantCanvas;
